/**
 * NotificationsInbox — the navbar bell, left of the profile avatar.
 *
 * Shows an unread badge and a dropdown of the user's notifications
 * (scrape complete/failed today; the same inbox absorbs future kinds like
 * sponsor outreach). Rows are persisted server-side at event time, so a
 * scrape finishing while the user was signed out still lands here.
 *
 * Polling: light — unread refresh every 60s + on window focus. Opening
 * the panel fetches fresh. No websockets at current scale.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  BellIcon, ChartBarIcon, CheckCircleIcon, ChatAlt2Icon, ExclamationIcon, SparklesIcon,
} from '@heroicons/react/outline';
import {
  AppNotification,
  getNotifications,
  markNotificationsRead,
} from '../../services/notificationsService';
import { relativeTime } from '../../utils/time';

const POLL_MS = 60_000;


const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'scrape_complete')
    return <CheckCircleIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--green)' }} />;
  if (type === 'scrape_failed')
    return <ExclamationIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--amber)' }} />;
  // A brand replied — the payoff of the whole outreach loop. It read as a
  // generic sparkle, indistinguishable from a scrape finishing.
  if (type === 'outreach_reply')
    return <ChatAlt2Icon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--green)' }} />;
  if (type === 'outreach_bounce')
    return <ExclamationIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--red)' }} />;
  // A brand answered on the creator's deal link, or reported results there.
  if (type === 'deal_link_response')
    return <ChatAlt2Icon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--green)' }} />;
  if (type === 'deal_results_reported')
    return <ChartBarIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--blue)' }} />;
  return <SparklesIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--blue)' }} />;
};

const NotificationsInbox: React.FC = () => {
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      if (!token) return;
      const res = await getNotifications(token);
      setItems(res.notifications);
      setUnread(res.unread_count);
    } catch {
      /* best-effort — the bell never breaks the navbar */
    }
  }, [getToken, isSignedIn]);

  // Light polling + refresh on window focus.
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  // Click-outside or Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(items.length === 0);
      await refresh();
      setLoading(false);
    }
  };

  const onItemClick = (n: AppNotification) => {
    setOpen(false);
    // Navigate FIRST — a slow/stalled mark-read request must never block
    // the user from reaching the page. The write is fire-and-forget.
    if (n.link) navigate(n.link);
    if (!n.read) {
      setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      void (async () => {
        try {
          const token = await getToken();
          if (token) await markNotificationsRead(token, { ids: [n.id] });
        } catch { /* optimistic */ }
      })();
    }
  };

  const onMarkAll = async () => {
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      const token = await getToken();
      if (token) await markNotificationsRead(token, { all: true });
    } catch { /* optimistic */ }
  };

  if (!isSignedIn) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        className="group relative flex h-8 w-8 items-center justify-center rounded text-[color:var(--board-ink)] transition-colors hover:bg-white/10"
      >
        <BellIcon className="h-5 w-5 opacity-80 transition-opacity group-hover:opacity-100" />
        {unread > 0 && (
          <span className="br-count br-count--amber absolute -right-1.5 -top-1.5 leading-none" style={{ padding: '1px 4px', fontSize: 10 }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* The panel is a white folder pulled from the board; items are ruled rows on it. */}
      {open && (
        <div className="absolute right-0 top-full z-50 pt-2">
          <div className="br-folder-stock br-folder-stock--white w-[min(340px,calc(100vw-88px))] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="br-h3">Notifications</span>
              {unread > 0 && (
                <button type="button" onClick={onMarkAll} className="br-inkline text-label">
                  Mark all as read
                </button>
              )}
            </div>
            <hr className="br-hairline" />
            <div className="max-h-[380px] overflow-y-auto">
              {loading ? (
                <div className="space-y-3 p-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="br-empty h-10 animate-pulse" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BellIcon className="mx-auto h-7 w-7" style={{ color: 'var(--ink-4)' }} />
                  <p className="br-small mt-2">
                    Nothing yet. When an analysis you start finishes, you'll hear about it here.
                  </p>
                </div>
              ) : (
                <div className="br-ruled">
                  {items.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => onItemClick(n)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${n.read ? 'hover:bg-[var(--desk-lit)]' : 'bg-[var(--blue-wash)]'}`}
                    >
                      <TypeIcon type={n.type} />
                      <span className="min-w-0 flex-1">
                        <span className="br-h3 block truncate">
                          {n.title}
                        </span>
                        {n.body && (
                          <span className="br-small mt-0.5 block line-clamp-2">
                            {n.body}
                          </span>
                        )}
                        <span className="br-caption mt-1 block">
                          {relativeTime(n.created_at, { rolloverDays: 7 })}
                        </span>
                      </span>
                      {!n.read && <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: 'var(--blue)' }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsInbox;
